import {
  CalendarDateTime,
  type DateValue,
  getLocalTimeZone,
  parseDate,
  Time,
} from '@internationalized/date';
import { CalendarIcon, X } from 'lucide-react';
import { DateTime } from 'luxon';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { Matcher } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Calendar } from '@/shared/components/ui/calendar';
import { DateField, DateInput, TimeField } from '@/shared/components/ui/datefield';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { cn } from '@/shared/utils';

export const DatePicker = forwardRef<
  HTMLDivElement,
  {
    date?: Date | null;
    setDate: (date?: Date | null) => void;
    placeholder?: string;
    disabled?: boolean;
    defaultMonth?: Date | null;
    minDate?: Date;
    maxDate?: Date;
    disabledRanges?: Array<{ start: Date; end: Date }>;
    onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;
    includeTime?: boolean;
    hourCycle?: 12 | 24;
    granularity?: 'minute' | 'second';
    timeInline?: boolean;
    showClearButton?: boolean;
  }
>(function DatePickerCmp(
  {
    date,
    setDate,
    disabled = false,
    defaultMonth,
    minDate,
    maxDate,
    disabledRanges,
    onBlur,
    includeTime = false,
    hourCycle = 24,
    granularity = 'minute',
    showClearButton = true,
  },
  ref
) {
  const popoverTriggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation();
  const locale = i18n.language ?? 'en-US';

  // Convert JavaScript Date to @internationalized/date's DateValue
  const [dateValue, setDateValue] = useState<DateValue | null>(() => {
    if (date instanceof Date) {
      const jsTime = date.getTime();
      if (Number.isNaN(jsTime)) return null;
      const dt = DateTime.fromJSDate(date);
      if (!dt.isValid) return null;
      if (includeTime) {
        return new CalendarDateTime(dt.year, dt.month, dt.day, dt.hour, dt.minute, dt.second);
      }
      return parseDate(dt.toFormat('yyyy-MM-dd'));
    }
    return null;
  });

  const [timeValue, setTimeValue] = useState<Time | null>(() => {
    if (date && includeTime) {
      const dt = DateTime.fromJSDate(date);
      return new Time(dt.hour, dt.minute, dt.second);
    }
    return null;
  });

  useEffect(() => {
    if (date instanceof Date) {
      const jsTime = date.getTime();
      if (Number.isNaN(jsTime)) {
        setDateValue(null);
        setTimeValue(null);
        return;
      }
      const dt = DateTime.fromJSDate(date);
      if (!dt.isValid) {
        setDateValue(null);
        setTimeValue(null);
        return;
      }
      if (includeTime) {
        setDateValue(
          new CalendarDateTime(dt.year, dt.month, dt.day, dt.hour, dt.minute, dt.second)
        );
        setTimeValue(new Time(dt.hour, dt.minute, dt.second));
      } else {
        setDateValue(parseDate(dt.toFormat('yyyy-MM-dd')));
        setTimeValue(null);
      }
    } else {
      setDateValue(null);
      setTimeValue(null);
    }
  }, [date, includeTime]);

  const handleDateChange = (value: DateValue | null) => {
    if (!value) {
      setDate(undefined);
      return;
    }

    // Convert DateValue back to JavaScript Date
    const newDate = value.toDate(getLocalTimeZone());
    // Force midday for date-only mode to avoid DST shifts that can move the day
    if (!includeTime) {
      newDate.setHours(12, 0, 0, 0);
    }
    setDate(newDate);

    if (onBlur) {
      onBlur({
        target: {
          value: newDate,
        },
      } as unknown as React.FocusEvent<HTMLButtonElement>);
    }
  };

  const handleTimeChange = (value: Time | null) => {
    if (!value || !date) return;

    // Get current date values
    const currentDate = new Date(date);

    // Update with new time values
    currentDate.setHours(value.hour);
    currentDate.setMinutes(value.minute);
    if (granularity === 'second') {
      currentDate.setSeconds(value.second || 0);
    } else {
      currentDate.setSeconds(0);
    }

    setDate(currentDate);

    if (onBlur) {
      onBlur({
        target: {
          value: currentDate,
        },
      } as unknown as React.FocusEvent<HTMLButtonElement>);
    }
  };

  const resolvedDefaultMonth =
    (defaultMonth instanceof Date ? defaultMonth : undefined) ??
    (date instanceof Date ? date : undefined);

  const calendarDisabled = useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = [];

    if (minDate) {
      matchers.push({ before: minDate });
    }

    if (maxDate) {
      matchers.push({ after: maxDate });
    }

    if (disabledRanges?.length) {
      matchers.push(...disabledRanges.map(({ start, end }) => ({ from: start, to: end })));
    }

    return matchers.length ? matchers : undefined;
  }, [disabledRanges, maxDate, minDate]);

  return (
    <div className="relative flex w-full">
      <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
        <div className="relative flex w-full">
          <div className="relative w-full">
            <DateField
              value={dateValue}
              onChange={handleDateChange}
              isRequired={false}
              aria-label="Date"
              locale={locale}
              className={cn(
                'w-full border border-input rounded-md h-9 px-3 pt-[6px] text-left font-normal bg-background',
                !dateValue && 'text-muted-foreground',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <DateInput unstyled={true} className="outline-hidden focus:outline-hidden" />
            </DateField>
          </div>

          <div className="absolute right-0 flex space-x-0.5 mr-2.5">
            {date instanceof Date && showClearButton && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 px-2 w-6 hover:bg-transparent text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.preventDefault();
                  handleDateChange(null);
                }}
                disabled={disabled}
              >
                <X className="size-4" />
              </Button>
            )}
            <PopoverTrigger asChild>
              <Button
                ref={popoverTriggerRef}
                variant="ghost"
                size="icon"
                className={cn(
                  'h-9 px-2 w-6 hover:bg-transparent text-muted-foreground hover:text-foreground'
                )}
                disabled={disabled}
              >
                <CalendarIcon className="size-4" />
              </Button>
            </PopoverTrigger>
          </div>
        </div>
        <PopoverContent className="w-auto p-0" ref={ref} align="end">
          <Calendar
            mode="single"
            selected={date instanceof Date ? date : undefined}
            onSelect={(selectedDate) => {
              if (!selectedDate) {
                setDate(undefined);
                setIsOpen(false);
                return;
              }

              if (includeTime && date) {
                // Preserve the time from the existing date
                const newDate = new Date(selectedDate);
                newDate.setHours(date.getHours());
                newDate.setMinutes(date.getMinutes());
                newDate.setSeconds(date.getSeconds());
                setDate(newDate);
              } else {
                setDate(selectedDate);
              }

              if (!includeTime) {
                setIsOpen(false);
              }

              if (onBlur) {
                onBlur({
                  target: {
                    value: selectedDate,
                  },
                } as unknown as React.FocusEvent<HTMLButtonElement>);
              }
            }}
            initialFocus
            defaultMonth={resolvedDefaultMonth}
            captionLayout="dropdown"
            disabled={calendarDisabled}
            // Allow navigation across a wide range of years for planning purposes
            // 10 years in the past to 10 years in the future
            startMonth={new Date(new Date().getFullYear() - 10, 0)}
            endMonth={new Date(new Date().getFullYear() + 10, 11)}
          />

          {includeTime && (
            <div className="p-3 border-t border-border">
              <TimeField
                value={timeValue}
                onChange={(value) => {
                  handleTimeChange(value);
                  setTimeValue(value);
                }}
                hourCycle={hourCycle}
                granularity={granularity}
                aria-label="Time"
                className={
                  'w-[80px] flex justify-center border border-input rounded-md h-9 px-6 pt-[6px] text-left font-normal bg-background'
                }
              >
                <DateInput unstyled={true} className="outline-hidden focus:outline-hidden" />
              </TimeField>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
});
